import orderBy from "lodash/orderBy";
import { TeamMemberStatusType } from "@teaming/common-enums";
import { luxonify, sleep } from "@teaming/common-util";
import { alertFactory } from "@teaming/common-ui";

import * as listeners from "./listeners";
import { pristineState } from "./state";
import { newKeyResultForm } from "../forms";
import { SHORT_DATE_FORMAT, mapFormToKeyResult, mapKeyResultToForm, mapActivityToFeed } from "../mapping";

export default function keyResultStoreFactory( {
	storeName,
	foundation,
	appChannels,
	/** { store: { navigateToGoal:Function, inKeyResultRoute:Function.<Boolean>, }, ...navigation } */
	navigation,
} ) {
	const { store, i18n, ioSocket, ioTeam, ioGokr, } = foundation();
	const { ioChannel, } = appChannels;

	const { success, error, info, } = alertFactory( "common-gokr.stores.KeyResult", i18n.global.t );
	const alerts = { success, error, info, };

	listeners.configureSockets( {
		storeName,
		store,
		navigation,
		ioSocket,
		ioGokr,
		alerts,
	} );

	listeners.configureChannels( {
		storeName,
		store,
		navigation,
		ioChannel,
		alerts,
	} );

	return {
		namespaced: true,
		state: pristineState( i18n ),
		getters: {
			userProfile: _ => store.getters[ "host-userProfile/userProfile" ],
			timezone: _ => store.getters[ "host-userProfile/timezone" ],
			context( state ) {
				const { keyResultForm, } = state;
				const { teamId, goalId, keyResultId, } = keyResultForm;
				return {
					teamId,
					goalId,
					keyResultId,
				};
			},
			isBusy: state => state.isBusy,
			isDisabled: state => state.isDisabled,
			focusedField: state => state.focusedField,
			possibleKeyResultOwners( state ) {
				const { assignedTeam, } = state;
				const possibleOwners = assignedTeam.members
					.filter( member => member.status === TeamMemberStatusType.ACTIVE )
					.map( member => ( {
						...member,
						sortKey: member.displayName.toLowerCase(),
					} ) );
				return orderBy( possibleOwners, [ "sortKey", ], [ "asc", ] );
			},
			keyResultForm( state ) {
				const { parentGoal, } = state;
				return {
					...state.keyResultForm,
					goalId: parentGoal.id,
					parentGoalName: parentGoal.title,
					startDate: luxonify( parentGoal.plannedStartDate ).toFormat( SHORT_DATE_FORMAT ),
					endDate: luxonify( parentGoal.plannedFinishDate ).toFormat( SHORT_DATE_FORMAT ),
					sortOrder: parentGoal.keyResults.length,
				};
			},
			savePermitted( state ) {
				const { keyResultForm, } = state;
				const { allFieldsTouched, allFieldsValid, } = keyResultForm;
				return allFieldsTouched && allFieldsValid;
			},
			promptOnDiscard( state ) {
				return state.keyResultForm.isTouched;
			},
			materializedKeyResult( state, getters ) {
				return mapFormToKeyResult( getters.keyResultForm );
			},
			selectedPanel: state => state.selectedPanel,
			keyResultActivity( state, getters ) {
				const { goalActivity, } = state;
				const { timezone, keyResultForm, } = getters;

				const keyResultActivity = goalActivity.filter( activity =>
					activity.id === keyResultForm.keyResultId
				);

				const mappedActivity = mapActivityToFeed( keyResultActivity, timezone );
				return orderBy( mappedActivity, [ "sortKey", ], [ "desc", ] );
			},
			assignedTeam: state => state.assignedTeam,
			activityComment: state => state.activityComment,
		},
		mutations: {
			clearAll( state ) {
				Object.assign( state, pristineState( i18n ) );

				const {
					personId: ownerId,
					displayName: ownerName,
					email: ownerEmail,
					picture: ownerAvatar,
				} = store.getters[ "host-userProfile/userProfile" ];

				state.keyResultForm = newKeyResultForm( {
					...state.keyResultForm,
					ownerId,
					ownerName,
					ownerEmail,
					ownerAvatar,
				}, i18n );
			},
			pause( state, background = false ) {
				state.isBusy = true;
				state.isDisabled = !background;
			},
			resume( state ) {
				state.isBusy = false;
				state.isDisabled = false;
			},
			receiveParentGoal( state, parentGoal ) {
				state.parentGoal = parentGoal;
			},
			receiveAssignedTeam( state, assignedTeam ) {
				state.assignedTeam = assignedTeam;
			},
			receiveKeyResult( state, keyResult ) {
				state.keyResultForm = newKeyResultForm( {
					...mapKeyResultToForm( keyResult ),
				}, i18n );
			},
			receiveGoalActivity( state, goalActivity ) {
				state.goalActivity = goalActivity;
			},
			ensureValidation( state ) {
				state.saveAttempted = true;
				const { keyResultForm, } = state;
				state.keyResultForm = newKeyResultForm(
					keyResultForm, i18n, { touchAll: state.saveAttempted, }
				);
			},
			updateKeyResultForm( state, form ) {
				const { keyResultForm, assignedTeam, } = state;
				const { members, } = assignedTeam;
				const member = members.find( teamMember => teamMember.personId === form.ownerId );

				const {
					displayName: ownerName,
					email: ownerEmail,
					picture: ownerAvatar,
				} = member;

				state.keyResultForm = newKeyResultForm( {
					...keyResultForm,
					...form,
					ownerName,
					ownerEmail,
					ownerAvatar,
				}, i18n, {
					resetValues: form.type !== keyResultForm.type,
					touchAll: state.saveAttempted,
				} );
			},
			commitKeyResultForm( state, { keyResult, revision, } ) {
				const { focusedField, } = state;
				const { keyResultId, revision: currentRevision, } = state.keyResultForm;

				// if the key result already exists, and this update is for a different
				// key result, ignore the update
				if ( keyResultId && ( keyResultId !== keyResult.id ) ) {
					return;
				}

				// if the revision has changed, the key result was updated before
				// the save request could be completed, so we need to wait
				// until the revision matches
				if ( currentRevision !== revision ) {
					return;
				}

				state.keyResultForm = newKeyResultForm( mapKeyResultToForm( keyResult ), i18n );

				// preserve the focused field where possible
				if ( focusedField ) {
					const focusedFieldNameAndPosition = focusedField.split( "." )[ 1 ];
					const { formId, } = state.keyResultForm;
					state.focusedField = `${ formId }.${ focusedFieldNameAndPosition }`;
				}
			},
			focusField( state, { focusedFieldName, selectionStart = 0, } ) {
				const { formId, } = state.keyResultForm;
				state.focusedField = `${ formId }.${ focusedFieldName }:${ selectionStart }`;
			},
			selectPanel( state, panel ) {
				state.selectedPanel = panel;
				state.activityComment = "";
			},
			updateActivityComment( state, form ) {
				state.activityComment = form.comment;
			},
		},
		actions: {
			// eslint-disable-next-line no-unused-vars
			async initialize( { commit, }, { teamId = "", goalId = "", keyResultId = "", } ) {
				try {
					commit( "pause" );
					const promises = [
						ioTeam.fetchGoalTeam( { goalId, } ),
						ioGokr.fetchGoal( { goalId, } ),
						keyResultId ? ioGokr.fetchGoalActivity( { goalId, } ) : Promise.resolve(),
					];
					const [ teamResponse, goalResponse, activityResponse, ] = await Promise.all( promises );
					commit( "receiveAssignedTeam", teamResponse.data );
					commit( "receiveParentGoal", goalResponse.data );
					if ( keyResultId ) {
						const { keyResults, } = goalResponse.data;
						const keyResult = keyResults.find( kr => kr.id === keyResultId );
						if ( keyResult ) {
							commit( "receiveKeyResult", keyResult );
						}
						commit( "receiveGoalActivity", activityResponse.data );
					}
				} catch ( e ) {
					error( "unexpectedError", e );
				} finally {
					commit( "resume" );
				}
			},
			async trySaveKeyResult( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { assignedTeam, keyResultForm, materializedKeyResult: keyResult, } = getters;
					const { comment, } = keyResultForm;

					await ioGokr.saveKeyResult( { keyResult, } );
					const { id: teamId, } = assignedTeam;
					const { id: keyResultId, goalId, } = keyResult;

					const trimmedComment = comment.trim();
					if ( trimmedComment ) {
						// eslint-disable-next-line no-magic-numbers
						await sleep( 500 );
						await ioGokr.createKeyResultComment( { goalId, keyResultId, comment, } );
					}

					success( "createSuccessNotification" );
					// eslint-disable-next-line no-magic-numbers
					await sleep( 500 );
					navigation.store.navigateToGoal( { teamId, goalId, } );
				} catch ( e ) {
					error( "createErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async tryUpfetchKeyResult( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { keyResultForm, materializedKeyResult: keyResult, } = getters;
					const { comment, revision, } = keyResultForm;

					// ioGokr.upfetchKeyResult() is used to persist existing key results and then
					// immediately refetch the refreshed data
					const nextKeyResult = await ioGokr.upfetchKeyResult( { keyResult, } );
					const { id: keyResultId, goalId, } = nextKeyResult;

					const trimmedComment = comment.trim();
					if ( trimmedComment ) {
						// eslint-disable-next-line no-magic-numbers
						await sleep( 500 );
						await ioGokr.createKeyResultComment( { goalId, keyResultId, comment, } );
					}

					commit( "commitKeyResultForm", { keyResult: nextKeyResult, revision, } );
					success( "saveSuccessNotification" );
				} catch ( e ) {
					error( "saveErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async tryDeleteKeyResult( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { keyResultForm, } = getters;
					const { teamId, goalId, keyResultId, } = keyResultForm;

					await ioGokr.deleteKeyResult( { goalId, keyResultId, } );
					success( "deleteSuccessNotification" );
					// eslint-disable-next-line no-magic-numbers
					await sleep( 500 );
					navigation.store.navigateToGoal( { teamId, goalId, } );
				} catch ( e ) {
					error( "deleteErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async trySaveActivityComment( { getters, commit, } ) {
				try {
					commit( "pause" );
					const { activityComment: comment, context, } = getters;
					const { goalId, keyResultId, } = context;

					await ioGokr.createKeyResultComment( { goalId, keyResultId, comment, } );

					success( "createCommentSuccessNotification" );
					commit( "updateActivityComment", { comment: "", } );
				} catch ( e ) {
					error( "createCommentErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
		},
	};
}
